import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNavigateBack } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useSupplierQuotation, SupplierQuotationLine, SupplierQuotationExpense } from './useData';
import { moduleConfig } from './supplierQuotationsModuleConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { Accordion, AccordionItem, Button, Callout, Card, ChevronLeftIcon, CountChip } from '../../../components/ui';
import { supplyBlockedActionMessage } from '../blockingMessages';

const fmt2 = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/** Qty is stored/displayed without fractional decimals. */
const fmtQty = (n: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(n);

/** Match Form `asFinite`: non-finite → 0. */
const toFinite = (value: string | number | null | undefined): number => {
  const n = typeof value === 'number' ? value : value != null && value !== '' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : 0;
};

const computeUnitTotal = (line: SupplierQuotationLine): number => {
  const q = toFinite(line.qty);
  const p = toFinite(line.unitPrice);
  if (q !== 0 && p !== 0) return q * p;
  const t = toFinite(line.lineTotal);
  const d = toFinite(line.discount);
  const v = toFinite(line.vat);
  return t + d - v;
};

const computeCurrencyFromLineTotal = (lineTotal: string | number | null | undefined, rate: number): number =>
  toFinite(lineTotal) * rate;

const formatItemCell = (line: SupplierQuotationLine) => {
  const v = (line.itemVcode ?? '').trim() || '';
  const n = (line.itemDescr ?? '').trim() || '';
  return [v, n].filter(Boolean).join(' - ');
};

type QuotationLineSums = {
  unitTotal: number;
  discount: number;
  vat: number;
  total: number;
  currencyTotal: number;
};

const buildLineSums = (lineItems: SupplierQuotationLine[], rate: number): QuotationLineSums => {
  return lineItems.reduce<QuotationLineSums>(
    (acc, line) => {
      acc.unitTotal += computeUnitTotal(line);
      acc.discount += toFinite(line.discount);
      acc.vat += toFinite(line.vat);
      acc.total += toFinite(line.lineTotal);
      acc.currencyTotal += computeCurrencyFromLineTotal(line.lineTotal, rate);
      return acc;
    },
    { unitTotal: 0, discount: 0, vat: 0, total: 0, currencyTotal: 0 },
  );
};

type QuotationExpenseSums = { value: number; vat: number; currencyTotal: number };

const buildExpenseSums = (expenses: SupplierQuotationExpense[], rate: number): QuotationExpenseSums => {
  return expenses.reduce<QuotationExpenseSums>(
    (acc, e) => {
      const val = toFinite(e.value);
      const vat = toFinite(e.vat);
      acc.value += val;
      acc.vat += vat;
      acc.currencyTotal += (val + vat) * rate;
      return acc;
    },
    { value: 0, vat: 0, currencyTotal: 0 },
  );
};

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : NaN;
  const { item, isLoading, isError } = useSupplierQuotation(Number.isFinite(numericId) ? numericId : undefined);
  const { data: evaluationsRes } = useSWR<any>('/quotation-evaluations?page=1&limit=1000', fetcher);
  const { data: purchaseOrdersRes } = useSWR<any>('/supply-purchase-orders?page=1&limit=1000', fetcher);
  const { colors } = useTheme();
  const goBack = useNavigateBack(moduleConfig.module.route.list);
  const formatDate = (s?: string | null) => (s ? formatDateGlobal(s) : '');
  const currentQuotationId = item?.id ?? 0;
  const linkedOrderCount = React.useMemo(() => {
    if (!currentQuotationId) return 0;
    const rows = Array.isArray(purchaseOrdersRes) ? purchaseOrdersRes : purchaseOrdersRes?.data ?? [];
    return rows.filter((po: any) => Number(po?.quotationId) === currentQuotationId).length;
  }, [purchaseOrdersRes, currentQuotationId]);
  const linkedEvaluationCount = React.useMemo(() => {
    if (!currentQuotationId) return 0;
    const rows = Array.isArray(evaluationsRes) ? evaluationsRes : evaluationsRes?.data ?? [];
    return rows.filter((ev: any) => {
      const ids = [
        ev?.quotation1Id,
        ev?.quotation2Id,
        ev?.quotation3Id,
        ev?.quotation4Id,
        ev?.quotation5Id,
      ]
        .map((v) => (v == null ? null : Number(v)))
        .filter((v): v is number => Number.isFinite(v));
      const summaryByQuote = Array.isArray(ev?.summary?.byQuote) ? ev.summary.byQuote : [];
      const summaryIds = summaryByQuote
        .map((b: any) => Number(b?.quotationId))
        .filter((v: number) => Number.isFinite(v));
      return [...ids, ...summaryIds].includes(currentQuotationId);
    }).length;
  }, [evaluationsRes, currentQuotationId]);
  const editBlockedByEvaluationOrOrder = linkedEvaluationCount > 0 || linkedOrderCount > 0;

  if (!Number.isFinite(numericId)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid id.</p>;
  }
  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center rounded-xl border border-dashed px-4 py-16', colors.border)}>
        <p className={cn('text-sm font-medium', colors.textSecondary)}>Loading supplier quotation…</p>
      </div>
    );
  }
  if (isError || !item) {
    return (
      <Card variant="outlined" className={cn('p-6 sm:p-8', colors.border, 'max-w-md')}>
        <p className={cn('text-sm', colors.textSecondary)}>This quotation was not found or you no longer have access.</p>
        <Button type="button" variant="ghost" className="mt-4" onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4" />
          Back to list
        </Button>
      </Card>
    );
  }

  const lineCount = (item.lineItems || []).length;
  const expenseCount = (item.expenses || []).length;
  const supplierLabel = item.supplier
    ? item.supplier.code
      ? `${item.supplier.name} (${item.supplier.code})`
      : item.supplier.name
    : `Supplier #${item.supplierId}`;

  const currencyLabel = item.currency
    ? item.currency.symbol
      ? `${item.currency.code} (${item.currency.symbol}) — ${item.currency.name}`
      : `${item.currency.code} — ${item.currency.name}`
    : '';

  const rateN = toFinite(item.rate);
  const symbol = (item.currency?.symbol ?? '').trim();
  const currencyTotalCaption = symbol ? `(${symbol}) Total` : 'Total';
  const lineItems = item.lineItems || [];
  const expList = item.expenses || [];
  const lineSums = buildLineSums(lineItems, rateN);
  const expenseSums = buildExpenseSums(expList, rateN);
  const grandCurrencyTotal = lineSums.currencyTotal + expenseSums.currencyTotal;
  const quoteCcySubtotal = lineSums.total + expenseSums.value + expenseSums.vat;
  return (
    <div className="space-y-6">
      <Card
        variant="outlined"
        className={cn('overflow-hidden border shadow-sm', colors.border)}
      >
        <div
          className={cn(
            'flex flex-col gap-4 border-b px-4 py-4 sm:px-6 sm:py-5',
            colors.border,
            'bg-gradient-to-b from-slate-50/80 to-white dark:from-slate-900/40 dark:to-slate-900/20',
          )}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('-ml-2 gap-1.5 self-start font-medium', colors.text)}
                onClick={goBack}
              >
                <ChevronLeftIcon className="h-4 w-4 opacity-80" />
                Back
              </Button>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              {editBlockedByEvaluationOrOrder ? (
                <button
                  type="button"
                  disabled
                  title="This quotation cannot be edited while it is linked to a quotation evaluation or purchase order."
                  className={cn(
                    'inline-flex h-9 min-h-[2.25rem] shrink-0 cursor-not-allowed items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold opacity-60',
                    colors.primary,
                    colors.primaryText,
                  )}
                >
                  Edit
                </button>
              ) : (
                <Link
                  to={moduleConfig.module.route.edit(item.id)}
                  className={cn(
                    'inline-flex h-9 min-h-[2.25rem] shrink-0 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition-all hover:opacity-95',
                    colors.primary,
                    colors.primaryText,
                  )}
                >
                  Edit
                </Link>
              )}
            </div>
          </div>
        </div>
      </Card>
      {editBlockedByEvaluationOrOrder ? (
        <Callout variant="danger" title="Cannot edit quotation">{supplyBlockedActionMessage('QUO_EDIT_LINKED_EVAL_OR_ORDER')}</Callout>
      ) : null}

      <Card variant="outlined" className={cn('p-4 shadow-sm sm:p-5', colors.border)}>
        <Accordion className="space-y-3">
          <AccordionItem
            title="Quotation details"
            description="Quotation header fields (same order as edit), then supplier and supply case."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mt-2 pt-1 sm:pt-0 ">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 text-sm md:grid-cols-12">
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Quotation date</dt>
                  <dd className={cn('mt-1', colors.text)}>{formatDate(item.quoDate)}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Enquiry (RFQ)</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.enquiry?.id ? (
                      <Link
                        to={`/supplies/supplier-enquiries/${item.enquiry.id}`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.enquiry.ref || `RFQ #${item.enquiry.id}`}
                      </Link>
                    ) : (
                      <span className="tabular-nums">#{item.enqId}</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-2">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Reference</dt>
                  <dd className={cn('mt-1 break-words font-medium tabular-nums', colors.text)}>{item.ref || ''}</dd>
                </div>
                <div className="min-w-0 md:col-span-2">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Currency</dt>
                  <dd className={cn('mt-1', colors.text)}>{currencyLabel}</dd>
                </div>
                <div className="min-w-0 md:col-span-2">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Rate</dt>
                  <dd className={cn('mt-1 tabular-nums', colors.text)}>{item.rate != null && item.rate !== '' ? String(item.rate) : ''}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Supplier ref</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>{item.supplierRef?.trim() || ''}</dd>
                </div>
                <div className="min-w-0 md:col-span-5">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Payment terms</dt>
                  <dd className={cn('mt-1 min-h-[1.25rem] max-w-2xl break-words', colors.text)}>
                    {item.paymentTerms?.trim() ? item.paymentTerms : <span className={colors.textSecondary}>—</span>}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-4">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Notes</dt>
                  <dd
                    className={cn('mt-1 min-h-[1.25rem] max-w-2xl whitespace-pre-wrap break-words', colors.text)}
                  >
                    {item.notes?.trim() ? item.notes : <span className={colors.textSecondary}>—</span>}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Supplier</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>{supplierLabel}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Supply case</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.supplyCase?.id ? (
                      <Link
                        to={`/supplies/supply-cases/${item.supplyCase.id}/case-view`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.supplyCase.ref || `Case #${item.supplyCase.id}`}
                      </Link>
                    ) : item.caseId ? (
                      <span className="tabular-nums">#{item.caseId}</span>
                    ) : (
                      <span className={cn('italic', colors.textSecondary)}>—</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Expenses"
            description="Additional costs and VAT (per-line amounts use quotation currency, then rate × (value + VAT) for the converted column, same as lines)."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              expenseCount > 0 ? (
                <CountChip className="mr-1.5">
                  {expenseCount} line{expenseCount === 1 ? '' : 's'}
                </CountChip>
              ) : null
            }
          >
            <div className="-mx-1 overflow-x-auto sm:mx-0 sm:px-0">
              <table className="min-w-full text-sm">
                <thead>
                  <tr
                    className={cn(
                      'border-b text-left text-xs font-medium uppercase tracking-wider',
                      colors.border,
                      'bg-slate-50/95 dark:bg-slate-800/50',
                    )}
                  >
                    <th className="min-w-[10rem] px-2 py-2 text-left">Description</th>
                    <th className="w-28 px-2 py-2 text-right">Value</th>
                    <th className="w-28 px-2 py-2 text-right">VAT</th>
                    <th className="w-32 px-2 py-2 text-right">{currencyTotalCaption}</th>
                  </tr>
                </thead>
                <tbody>
                  {expList.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={cn('px-2 py-8 text-center text-sm', colors.textSecondary)}>
                        No expenses on this quotation.
                      </td>
                    </tr>
                  ) : (
                    expList.map((exp, i) => {
                      const v = toFinite(exp.value);
                      const vt = toFinite(exp.vat);
                      const ccy = (v + vt) * rateN;
                      return (
                        <tr
                          key={exp.id ?? `exp-${i}`}
                          className={cn(
                            'align-top',
                            'border-b transition-colors last:border-0',
                            colors.border,
                            'hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                          )}
                        >
                          <td className="p-2 text-left">
                            <span className={colors.text}>{exp.description || ''}</span>
                          </td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(v)}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(vt)}</td>
                          <td className="p-2 text-right font-medium tabular-nums text-indigo-700 dark:text-indigo-300">
                            {fmt2(ccy)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {expList.length > 0 ? (
                  <tfoot>
                    <tr className={cn('border-t', colors.border, colors.surfaceHover)}>
                      <td colSpan={1} className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}>
                        SUM
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Value sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(expenseSums.value)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>VAT sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(expenseSums.vat)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div
                          className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}
                        >
                          {currencyTotalCaption} sum
                        </div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right text-indigo-700 dark:text-indigo-300')}>
                          {fmt2(expenseSums.currencyTotal)}
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Line items"
            description="Columns match the edit form: line total × rate for the converted column; footer sums match the form."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              lineCount > 0 ? (
                <CountChip className="mr-1.5">
                  {lineCount} line{lineCount === 1 ? '' : 's'}
                </CountChip>
              ) : null
            }
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr
                    className={cn('border-b text-left text-xs font-medium uppercase tracking-wider', colors.border, 'bg-slate-50/95 dark:bg-slate-800/50')}
                  >
                    <th className="w-10 px-2 py-2 text-left tabular-nums">#</th>
                    <th className="w-[10rem] min-w-[8rem] px-2 py-2 text-left">Level 1</th>
                    <th className="min-w-[12rem] px-2 py-2 text-left">Item</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Unit</th>
                    <th className="w-24 px-2 py-2 text-right">Qty</th>
                    <th className="w-24 px-2 py-2 text-right">Price</th>
                    <th className="w-24 px-2 py-2 text-right">Unit total</th>
                    <th className="w-24 px-2 py-2 text-right">Discount</th>
                    <th className="w-24 px-2 py-2 text-right">VAT</th>
                    <th className="w-24 px-2 py-2 text-right">Total</th>
                    <th className="w-32 px-2 py-2 text-right">{currencyTotalCaption}</th>
                    <th className="w-[12rem] min-w-[8rem] px-2 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr>
                      <td colSpan={12} className={cn('px-2 py-8 text-center text-sm', colors.textSecondary)}>
                        No line items on this quotation.
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((line, idx) => {
                      const ut = computeUnitTotal(line);
                      const d = toFinite(line.discount);
                      const v = toFinite(line.vat);
                      const t = toFinite(line.lineTotal);
                      const ccy = computeCurrencyFromLineTotal(line.lineTotal, rateN);
                      return (
                        <tr
                          key={line.id ?? `${line.itemId}-${line.itemDescr}`}
                          className={cn('align-top border-b transition-colors last:border-0', colors.border, 'hover:bg-slate-50/80 dark:hover:bg-slate-800/40')}
                        >
                          <td className="p-2 text-left tabular-nums">{idx + 1}</td>
                          <td className="p-2 text-left">{(line.item?.level1 ?? '').trim() || ''}</td>
                          <td className="p-2 text-left">{formatItemCell(line)}</td>
                          <td className="p-2 text-right">{(line.unit?.ucode ?? '').trim() || ''}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmtQty(toFinite(line.qty))}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(toFinite(line.unitPrice))}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(ut)}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(d)}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(v)}</td>
                          <td className="p-2 text-right font-medium tabular-nums">{fmt2(t)}</td>
                          <td className="p-2 text-right font-semibold tabular-nums text-indigo-700 dark:text-indigo-300">{fmt2(ccy)}</td>
                          <td className="p-2 text-left">{line.itemNotes || ''}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {lineItems.length > 0 ? (
                  <tfoot>
                    <tr className={cn('border-t', colors.border)}>
                      <td colSpan={6} className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}>
                        SUM
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Unit total sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.unitTotal)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Discount sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.discount)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>VAT sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.vat)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Total sum</div>
                        <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.total)}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>{currencyTotalCaption} sum</div>
                        <div
                          className={cn('text-sm font-semibold tabular-nums text-right text-indigo-700 dark:text-indigo-300')}
                        >
                          {fmt2(lineSums.currencyTotal)}
                        </div>
                      </td>
                      <td className="px-2" />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </AccordionItem>
        </Accordion>

        {lineCount > 0 || expenseCount > 0 ? (
          <div
            className={cn(
              'mt-4 rounded-lg border px-4 py-3 text-sm sm:px-5',
              colors.border,
              'bg-slate-50/90 dark:bg-slate-800/30',
            )}
          >
            <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textSecondary)}>Quotation roll-up</h3>
            <dl className="mt-2 space-y-1.5 sm:grid sm:grid-cols-1 sm:gap-2 sm:space-y-0">
              {lineCount > 0 ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 sm:gap-4">
                  <dt className="text-slate-600 dark:text-slate-400">Lines subtotal (quotation currency)</dt>
                  <dd className="font-medium tabular-nums">{fmt2(lineSums.total)}</dd>
                </div>
              ) : null}
              {expenseCount > 0 ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 sm:gap-4">
                  <dt className="text-slate-600 dark:text-slate-400">Expenses (value + VAT, quotation currency)</dt>
                  <dd className="font-medium tabular-nums">{fmt2(expenseSums.value + expenseSums.vat)}</dd>
                </div>
              ) : null}
              {lineCount > 0 && expenseCount > 0 ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-dashed pt-2 sm:gap-4">
                  <dt className="font-medium text-slate-700 dark:text-slate-300">Combined (quotation currency)</dt>
                  <dd className="font-semibold tabular-nums">{fmt2(quoteCcySubtotal)}</dd>
                </div>
              ) : null}
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-t pt-2 sm:gap-4">
                <dt className="font-medium text-slate-800 dark:text-slate-200">Grand total ({currencyTotalCaption}, lines + expenses)</dt>
                <dd className="text-base font-bold tabular-nums text-indigo-700 dark:text-indigo-300">
                  {fmt2(grandCurrencyTotal)}
                </dd>
              </div>
            </dl>
            <p className={cn('mt-2 text-xs', colors.textSecondary)}>Line totals: line total × rate (same as edit). Expenses: (value + VAT) × rate.</p>
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export default Detail;
